<template>
    <div class="container">
        <div class="sectionHeadline text-h6 mb-4">Service descriptions</div>
        <v-form v-model="valid" ref="form">
            <Textarea label="General Description" v-model="localData.description" rows="5"
                      :disabled="$store.state.readOnly === 1 || !allowEdit"
                      :rules="rules.description" @blur="handleChange"></Textarea>
            <div class="mb-3 mt-6"><i>{{ $store.state.details.combobox_hint }}</i></div>
            <div :key="forceReRender" class="mb-4">
                <transition-group name="slide-y-transition">
                    <component v-for="(component, i) in dropdowns" v-model="localData.services[i]" :is="component.name"
                               :items="filteredServices()" :hideDelete="i === dropdowns.length - 1"
                               :rules="i === 0 ? rules.services : []" :multiple="false"
                               @change="addToSelected" :allowEdit="allowEdit"
                               :key="component.name+'_'+i" :index="i" @click:clear="deleteService"></component>
                </transition-group>
            </div>
        </v-form>
    </div>
</template>

<script>
import Textarea from "../../../components/Inputs/Textarea";
import ServiceDropdownRow from "../ServiceDropdownRow";

export default {
    name: "Services",
    props: ['data', 'tabIndex', 'previousIndex', 'tabs', 'allowEdit'],
    components: {ServiceDropdownRow, Textarea},
    data: () => ({
        dropdowns: [{name: 'ServiceDropdownRow'}],
        search: null,
        valid: null,
        forceReRender: 0,
        localData: {
            description: null,
            services: [],
        },
    }),
    mounted() {
        this.init();
        this.$emit('mounted', this);
    },
    computed: {
        vuexState() {
            return this.$store.state.details;
        },
        services() {
            return this.vuexState.services;
        },
        rules() {
            return {
                description: [v => !!v || 'General Description is required'],
                services: [this.localData.services.length !== 0 || 'Please select at least one description']
            }
        },
        hasChanges() {
            return this.$store.state.details.components.services.hasChanges;
        },
        role() {
            return this.$store.state.users.current ? this.$store.state.users.current.role : null;
        },
        allReqFieldsNotFilled() {
            return (
                this.localData.description === null &&
                this.localData.services.length === 0
            )
        }
    },
    methods: {
        init() {
            if (this.vuexState.services.length === 0) this.fetchServices();
            this.localData = {
                description: this.data && typeof this.data.description !== 'undefined' ? this.data.description : null,
                services: this.data && this.data.services ? JSON.parse(JSON.stringify(this.data.services)) : [],
            };
            if (this.data) {
                // reset to 0 first to delete old Data in Dropdown
                this.resetDropdowns();
                this.dropdowns = [{name: 'ServiceDropdownRow'}];
                for (let i = 0; i < this.localData.services.length; i++) {
                    this.dropdowns.push({name: 'ServiceDropdownRow'});
                    if ((this.$store.state.readOnly === 1 || !this.allowEdit) && i === this.localData.services.length - 1) {
                        this.dropdowns.pop();
                    }
                }
            }
        },
        filteredServices() {
            let ret = JSON.parse(JSON.stringify(this.services));
            for (let i = 0; i < ret.length; i++) {
                if (this.localData.services.map(x => x.name).includes(ret[i].name)) {
                    ret.splice(i, 1);
                    i--;
                }
            }
            return ret;
        },
        async fetchServices() {
            if (!this.$store.state.details.fetching.services) {
                await this.$store.dispatch('details/fetchServices');
            }
        },
        addToSelected(obj) {
            let index = obj.service_old ? this.localData.services.findIndex(x => x.id === obj.service_old.id) : -1;
            obj.service_new.pivot = {
                service_id: obj.service_new.id,
                priority: obj.priority
            }
            if (index !== -1) {
                this.localData.services[index] = obj.service_new;
                this.forceReRender++;
            } else if (this.localData.services.findIndex(x => x.name === obj.service_new.name) === -1) {
                this.localData.services.push(obj.service_new);
                this.dropdowns.push({name: 'ServiceDropdownRow'});
            }
            this.handleChange();
        },
        deleteService(service) {
            if (this.dropdowns.length > 1) {
                this.dropdowns.pop();
            }
            if (this.localData.services.findIndex(x => x.name === service.name) !== -1) {
                this.localData.services.splice(this.localData.services.findIndex(x => x.name === service.name), 1);
                for (let i = 0; i < this.localData.services.length; i++) {
                    this.localData.services[i].pivot.priority = i + 1;
                }
            }
            this.forceReRender++;
            this.handleChange();
        },
        resetDropdowns() {
            this.dropdowns = [];
            this.forceReRender++;
        },
        handleChange() {
            let role = this.role === 'agency_group' ? 'agency' : this.role;
            let og_data = this.role === 'admin' || this.role === 'user' ?
                this.$store.state.agency_vendor.selectedData.details :
                this.$store.state.details.data[role].details;
            if (og_data === null) og_data = JSON.parse(JSON.stringify(this.$store.state.details.details_template));
            let changed = false;
            if (og_data.services.length !== 0 && og_data.services.length === this.localData.services.length) {
                for (let i = 0; i < og_data.services.length; i++) {
                    if (og_data.services[i].id !== this.localData.services[i].id ||
                        og_data.services[i].pivot.priority !== this.localData.services[i].pivot.priority) {
                        changed = true;
                        break;
                    }
                }
            }
            if (og_data.description !== this.localData.description ||
                og_data.services.length !== this.localData.services.length ||
                changed
            ) {
                if (!this.hasChanges)
                    this.$store.commit('details/updateHasChanges', {section: 'services', value: true});
            } else {
                if (this.hasChanges)
                    this.$store.commit('details/updateHasChanges', {section: 'services', value: false});
            }
        }
    },
    watch: {
        localData: {
            deep: true,
            handler(v) {
								if (JSON.stringify(this.data) === JSON.stringify({...this.data, ...v})) {
									return;
								}
								this.$emit('update:data', {...this.data, ...v});
            }
        },
        data() {
            this.init()
            if (!this.$store.state.details.isRegistration) {
                setTimeout(() => {
                    this.$emit('validate', 'Services');
                }, 500);
            }
        },
        valid() {
            this.$emit('validate', 'Services');
        },
        allowEdit() {
            this.init();
        }
    }
}
</script>

<style scoped>

</style>